import axios from 'axios';

const API_URL = 'https://quiz.tvlist.fun';

// Создаем экземпляр axios с базовой конфигурацией
const api = axios.create({
    baseURL: API_URL,
});

interface UserProfile {
    id: string;
    email: string;
    image_url: string;
}


let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

api.interceptors.request.use(
    async (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        if (error.response.status === 401 && !originalRequest._retry) {
            console.log(error.response.data.detail)
            if (error.response.data.detail === "Invalid refresh token"){
                logoutUser();
            }
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({resolve, reject});
                }).then(token => {
                    originalRequest.headers['Authorization'] = 'Bearer ' + token;
                    return api(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            return new Promise((resolve, reject) => {
                const refresh_token = localStorage.getItem('refresh_token');
                api.post('/refresh', { refresh_token })
                    .then(response => {
                        const newToken = response.data.access_token;
                        localStorage.setItem('access_token', newToken);
                        localStorage.setItem('refresh_token', response.data.refresh_token);
                        api.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
                        originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
                        processQueue(null, newToken);
                        resolve(api(originalRequest));
                    })
                    .catch(err => {
                        processQueue(err, null);
                        logoutUser();
                        reject(err);
                    })
                    .finally(() => {
                        isRefreshing = false;
                    });
            });
        }
        return Promise.reject(error);
    }
);

const logoutUser = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('refresh_token');
    window.location.href = '/login';
};


// Функция для авторизации и получения токена
export const login = async (username: string, password: string) => {
    try {
        const response = await api.post('/token', {username, password});
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
        localStorage.setItem('user_id', response.data.user_id);
        return response.data;
    } catch (error) {
        console.error('Login error:', error);
        throw error; // Проброс ошибки для обработки в компоненте
    }
};

// Функция для авторизации и получения токена
export const forgot = async (email: string) => {
    try {
        const response = await api.post('/password_reset/request', {email});
        return response.data;
    } catch (error) {
        console.error('Login error:', error);
        throw error; // Проброс ошибки для обработки в компоненте
    }
};

// Функция для регистрации
export const register = async (email: string, password: string) => {
    try {
        const response = await api.post('/users/', {email, password});
        return response.data;
    } catch (error) {
        console.error('Registration error:', error);
        throw error;
    }
};

// Функция для подтверждения email
export const confirmEmailPath = async (token: string) => {
    try {
        const response = await api.get('/users/confirm-email', {
            params: {token},
        });
        return response.data;
    } catch (error) {
        console.error('Email confirmation error:', error);
        throw error;
    }
};

export const resetPassword = async (email: string, otp: string, newPassword: string) => {
    const response = await api.post('/password_reset/confirm', { email, otp, new_password: newPassword });
    return response.data;
};

// Функция для проверки аутентификации
export const isAuthenticated = () => {
    return !!localStorage.getItem('access_token');
};

// Функция для выхода из системы
export const logout = () => {
    localStorage.removeItem('access_token');
};


export const getUserProfile = async () => {
    const response = await api.get('/users/me/');
    return response.data;
};

export const updateProfile = async (profile: UserProfile) => {
    const response = await api.put(`/users/${profile.id}`, profile);
    return response.data;
};

export const changePassword = async (old_password: string, new_password: string) => {
    const response = await api.post('/users/change_password', { old_password, new_password });
    return response.data;
};

export const uploadUserAvatar = async (userId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
        const response = await api.post(`/users/${userId}/upload-image`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading avatar:', error);
        throw error;
    }
};

export const addQuiz = async (data: any) => {
    try {
        const response = await api.post('/quizzes/', data);
        return response.data;
    } catch (error) {
        console.error('Error adding quiz:', error);
        throw error;
    }
};
export const getQuizzes = async (params: {
    page: number;
    limit: number;
    quiz_type: number;
    sort_by: string;
    sort_order: string;
}) => {
    try {
        const response = await api.get('/quizzes/', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching quizzes:', error);
        throw error;
    }
};
export const getAllQuizzes = async (params: {
    page: number;
    limit: number;
    sort_by: string;
    sort_order: string;
}) => {
    try {
        const response = await api.get('/quizzes/', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching quizzes:', error);
        throw error;
    }
};

export const getQuizById = async (quizId: string) => {
    try {
        const response = await api.get(`/quizzes/${quizId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching quiz by ID:', error);
        throw error;
    }
};

export const updateQuiz = async (quizId: string, data: any) => {
    try {
        const response = await api.put(`/quizzes/${quizId}`, data);
        return response.data;
    } catch (error) {
        console.error('Error updating quiz:', error);
        throw error;
    }
};
export const deleteQuiz = async (quizId: string) => {
    try {
        const response = await api.delete(`/quizzes/${quizId}`);
        return response.data;
    } catch (error) {
        console.error('Error updating quiz:', error);
        throw error;
    }
};
export const uploadQuizImage = async (quizId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
        const response = await api.post(`/quizzes/upload_image/${quizId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading quiz image:', error);
        throw error;
    }
};
export const uploadPatQuizImage = async (quizId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
        const response = await api.post(`/quizzes/upload_patient_photo/${quizId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading quiz image:', error);
        throw error;
    }
};

export const getQuestions = async (params: {
    page: number;
    limit: number;
    quiz_id: string;
    section: number;
    sort_by: string;
    sort_order: string;
}) => {
    try {
        const response = await api.get('/questions/', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching questions:', error);
        throw error;
    }
};

export const addQuestion = async (data: {
    title: string;
    section: number;
    quiz_id: string;
    nocorrect: boolean;
    sort: number;
}) => {
    try {
        const response = await api.post('/questions/', data);
        return response.data;
    } catch (error) {
        console.error('Error adding question:', error);
        throw error;
    }
};

export const updateQuestion = async (questionId: string, data: any) => {
    try {
        const response = await api.put(`/questions/${questionId}`, data);
        return response.data;
    } catch (error) {
        console.error('Error updating quiz:', error);
        throw error;
    }
};
export const updateQuestionSortOrder = async (questionIds: string[]) => {
    try {
        const response = await api.post('/questions/update_sort_order', { question_ids: questionIds });
        return response.data;
    } catch (error) {
        console.error('Error updating question sort order:', error);
        throw error;
    }
};

export const uploadQuestionImage = async (questionId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
        const response = await api.post(`/questions/upload_image/${questionId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading question image:', error);
        throw error;
    }
};

export const addAnswer = async (data: {
    text: string;
    question_id: string;
    is_correct: boolean;
    sort: number;
}) => {
    try {
        const response = await api.post('/answers/', data);
        return response.data;
    } catch (error) {
        console.error('Error adding answer:', error);
        throw error;
    }
};

export const updateAnswer = async (answerId: string, data: any) => {
    try {
        const response = await api.put(`/answers/${answerId}`, data);
        return response.data;
    } catch (error) {
        console.error('Error updating quiz:', error);
        throw error;
    }
};

export const uploadAnswerImage = async (answerId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
        const response = await api.post(`/answers/upload_image/${answerId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading answer image:', error);
        throw error;
    }
};

export const deleteQuestion = async (questionId: string) => {
    try {
        const response = await api.delete(`/questions/${questionId}`);
        return response.data;
    } catch (error) {
        console.error('Error updating quiz:', error);
        throw error;
    }
};

export const deleteAnswer = async (answerId: string) => {
    try {
        const response = await api.delete(`/answers/${answerId}`);
        return response.data;
    } catch (error) {
        console.error('Error updating quiz:', error);
        throw error;
    }
};


// services/api.ts
export const getMediaBlocks = async (quiz_id: string, section: number, isBefore: boolean) => {
    try {
        const response = await api.get('/media_blocks/', {
            params: { quiz_id: quiz_id, section: section, is_before: isBefore },
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching media blocks:', error);
        throw error;
    }
};

export const addMediaBlock = async (data: { quiz_id: string; section: number; title: string; description: string; is_before: boolean }) => {
    try {
        const response = await api.post('/media_blocks/', data);
        return response.data;
    } catch (error) {
        console.error('Error adding media block:', error);
        throw error;
    }
};

export const updateMediaBlock = async (blockId: string, data: { quiz_id: string; section: number; title: string; description: string; is_before: boolean }) => {
    try {
        const response = await api.put(`/media_blocks/${blockId}`, data);
        return response.data;
    } catch (error) {
        console.error('Error updating media block:', error);
        throw error;
    }
};

export const deleteMediaBlock = async (blockId: string) => {
    try {
        await api.delete(`/media_blocks/${blockId}`);
    } catch (error) {
        console.error('Error deleting media block:', error);
        throw error;
    }
};

export const deletePhoto = async (deletePhoto: string) => {
    try {
        await api.delete(`/photos/${deletePhoto}`);
    } catch (error) {
        console.error('Error deleting media block:', error);
        throw error;
    }
};

export const uploadPhoto = async (mediaBlockId: string, formData: FormData) => {
    try {
        const response = await api.post(`/photos/upload_image`, formData, {
            params: { media_block_id: mediaBlockId },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading photo:', error);
        throw error;
    }
};

export const uploadFreePhoto = async (formData: FormData) => {
    try {
        const response = await api.post(`/photos/upload_free_image`, formData, {
            params: {},
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading photo:', error);
        throw error;
    }
};

// services/api.ts
export const fetchStatistics = async (quiz_id: string) => {
    try {
        var response = null
        if (quiz_id === "Все"){
            response = await api.get('/statistics/summary');
        }else{
            response = await api.get('/statistics/summary', {
                params: { quiz_id: quiz_id},
            });
        }

        return response.data;
    } catch (error) {
        console.error('Error fetching media blocks:', error);
        throw error;
    }
};


export const fetchStatisticsTable = async (
    page: number,
    limit: number,
    quiz_id: string,
    date_from: string,
    date_to: string
) => {
    try {
        const params = {
            page,
            limit,
            ...(quiz_id && quiz_id !== "Все" && { quiz_id }), // Добавляем quiz_id только если он есть и не "Все"
            ...(date_from && { date_from }), // Добавляем date_from только если есть значение
            ...(date_to && { date_to }) // Добавляем date_to только если есть значение
        };

        const response = await api.get('/statistics/', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching statistics:', error);
        throw error;
    }
};


export default api;
